import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown, BookOpenCheck, Trash2, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { api } from "@/lib/api";
import { useAuth, formatError } from "@/lib/auth";

const Ctx = createContext(null);

/** Workgroup log of Good/Bad verdicts on LLM-written wordings (the LLM reads it in later sessions). */
export function WordingFeedbackProvider({ children }) {
  const [items, setItems] = useState([]);
  const reload = useCallback(() => api.listWordingFeedback().then(setItems).catch((e) => console.warn("wording feedback load failed", e)), []);
  useEffect(() => { reload(); }, [reload]);
  const add = async (payload) => { const rec = await api.addWordingFeedback(payload); setItems((xs) => [rec, ...xs]); return rec; };
  const remove = async (id) => { await api.deleteWordingFeedback(id); setItems((xs) => xs.filter((x) => x.id !== id)); };
  return <Ctx.Provider value={{ items, add, remove, reload }}>{children}</Ctx.Provider>;
}

const norm = (t) => (t || "").trim().replace(/\s+/g, " ");

/** Good / Bad + short note on one suggested wording. */
export function WordingFeedback({ source, text, ruleName, ruleType, intent, testId }) {
  const ctx = useContext(Ctx);
  const me = useAuth()?.user;
  const [verdict, setVerdict] = useState(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  if (!ctx || !norm(text)) return null;
  const mine = ctx.items.find((x) => x.source === source && norm(x.text) === norm(text) && x.author?.id === me?.id);
  const others = ctx.items.filter((x) => norm(x.text) === norm(text) && x.author?.id !== me?.id);

  const save = async () => {
    setError(null);
    try {
      await ctx.add({ source, text, verdict, note, rule_name: ruleName, rule_type: ruleType, intent });
      setVerdict(null);
      setNote("");
    } catch (e) {
      setError(formatError(e));
    }
  };

  return (
    <div className="mt-2 space-y-1.5" data-testid={testId}>
      {others.map((o) => (
        <p key={o.id} className="text-[11px] text-muted-foreground">
          <Verdict v={o.verdict} /> by {o.author?.name}{o.note && <>: <em>{o.note}</em></>}
        </p>
      ))}
      {mine ? (
        <div className="flex items-center gap-2 text-xs">
          <Verdict v={mine.verdict} /> <span className="text-muted-foreground">by you{mine.note && <>: <em>{mine.note}</em></>} — the LLM will learn from this</span>
          <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => ctx.remove(mine.id)} data-testid={`${testId}-undo`}><Undo2 className="w-3 h-3 mr-1" /> Undo</Button>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] text-muted-foreground">Is this wording right?</span>
          <Button size="sm" variant={verdict === "good" ? "default" : "outline"} className="h-6 text-[11px]" onClick={() => setVerdict("good")} data-testid={`${testId}-good`}><ThumbsUp className="w-3 h-3 mr-1" /> Good</Button>
          <Button size="sm" variant={verdict === "bad" ? "destructive" : "outline"} className="h-6 text-[11px]" onClick={() => setVerdict("bad")} data-testid={`${testId}-bad`}><ThumbsDown className="w-3 h-3 mr-1" /> Bad</Button>
          {verdict && (
            <>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={verdict === "good" ? "What's right about it? (optional)" : "What's wrong? e.g. too strong, wrong keyword…"} className="h-7 text-xs flex-1 min-w-[200px]" data-testid={`${testId}-note`} />
              <Button size="sm" className="h-7 text-xs" onClick={save} data-testid={`${testId}-save`}>Save</Button>
            </>
          )}
        </div>
      )}
      {error && <p className="text-[11px] text-rose-300">{error}</p>}
    </div>
  );
}

function Verdict({ v }) {
  return v === "good"
    ? <Badge variant="outline" className="text-[10px] text-emerald-300 border-emerald-700/50"><ThumbsUp className="w-3 h-3 mr-1" />Good</Badge>
    : <Badge variant="outline" className="text-[10px] text-rose-300 border-rose-700/50"><ThumbsDown className="w-3 h-3 mr-1" />Bad</Badge>;
}

/** The workgroup's feedback log, newest first. */
export function WordingFeedbackLog() {
  const ctx = useContext(Ctx);
  const me = useAuth()?.user;
  if (!ctx) return null;
  return (
    <Collapsible>
      <CollapsibleTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5" data-testid="wording-log-toggle">
          <BookOpenCheck className="w-3.5 h-3.5" /> Wording feedback log ({ctx.items.length})
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 rounded-md border border-border bg-card/40 p-3 max-h-80 overflow-y-auto space-y-2" data-testid="wording-log">
        <p className="text-[11px] text-muted-foreground">The LLM reads the latest 20 entries every time it writes or revises a rule for this workgroup.</p>
        {!ctx.items.length && <p className="text-xs text-muted-foreground">No feedback yet — mark suggested wordings Good or Bad.</p>}
        {ctx.items.map((it) => (
          <div key={it.id} className="text-xs border-t border-border pt-2 first:border-0 first:pt-0" data-testid={`wording-log-item-${it.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Verdict v={it.verdict} />
              <span className="text-muted-foreground">{it.source === "draft" ? "Write from intent" : "Revision"}{it.rule_name ? ` · ${it.rule_name}` : ""} · {it.author?.name} · {new Date(it.created_at).toLocaleString()}</span>
              {(me?.role === "admin" || it.author?.id === me?.id) && (
                <button type="button" onClick={() => ctx.remove(it.id)} className="ml-auto text-muted-foreground hover:text-rose-300" data-testid={`wording-log-delete-${it.id}`}><Trash2 className="w-3 h-3" /></button>
              )}
            </div>
            <p className="mt-1 text-foreground/85">&ldquo;{it.text}&rdquo;</p>
            {it.note && <p className="text-muted-foreground italic">{it.note}</p>}
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}
